/**
 * 错词复习排程 —— **纯函数，全项目唯一的那一处规则**（第四步的核心价值）。
 *
 * ## 为什么必须是"一个纯函数 + 一份类型"
 *
 * 这条规则会同时被三处使用：
 *   1. **服务层**：批改提交后决定"这个词的下次复习时间是哪天"；
 *   2. **数据库约束**：`dictation_mistake` 的 CHECK 必须与它同口径
 *      （`review_stage` 0~3、`next_review_at` 与 `resolved_at` 互斥）；
 *   3. **界面**：要用人话告诉家长"下次什么时候复习、为什么是这个时间"。
 *
 * 三处各写一遍，迟早会漂移成"界面说 3 天后、库里排的是 7 天后"——
 * 而这正是家长最会察觉、也最伤信任的那种错（他以为系统在乱排）。
 * 所以：**阶梯、上限、判定的判定全部只在这里定义**，其它地方只调用。
 *
 * ## 规则本身（用家长的话说）
 *
 * > 错的词会自己排进下一次听写：错一次 → **1 天后**再来；
 * > 又错 → **3 天后**；再错 → **7 天后**（到顶就一直 7 天）。
 * > 在复习中**连续答对 2 次**就算掌握了，从错词本里消解，不再自动排进来。
 *
 * 两个刻意的取舍：
 *
 *   · **错 1 次只需 1 天**（而不是"隔一周"）：孩子刚写错的那个字，第二天
 *     趁热再写一遍最有效；等到一周后他早就忘了当时错在哪。
 *   · **消解要连续答对 2 次**（而不是 1 次）：只对一次就消解，会让"蒙对"也算
 *     掌握 —— 而这个模块存在的全部意义就是**别让错的词悄悄溜走**。
 *     连续两次把"碰巧写对"滤掉，代价只是多复习一天。
 *
 * ## `wrong` 与 `missing` 的区别（以及为什么对排程**没有**区别）
 *
 * | mark | 意思 | 记进错词本吗 | 排程 |
 * | --- | --- | --- | --- |
 * | `wrong` | 写了，但写错了 | 是（`wrong_count` +1） | 推进一档 |
 * | `missing` | **没写出来**（不会写 / 空着） | 是（`missing_count` +1） | 推进一档 |
 * | `correct` | 对了 | 连续答对 +1，到 2 次就消解 | 保留在阶梯上或消解 |
 *
 * 两者**都**要重听（用户原话："没有听写出来或者错的词"，两件事都要登记），
 * 所以对**排程**是同一档推进 —— 差别只在计数：家长在界面上要能看出
 * "这个词是根本不会写"还是"会写但写错了"，那是两种不同的辅导方式。
 *
 * ## 反悔（家长批改点错了）
 *
 * `transitionMistake(state, null)` 是**回退**：把上一次批改对这条错词的影响撤掉。
 * 它与正向操作**互逆**（`nextReviewAt` 会回到之前那一档），所以"点错 → 改成对的"
 * 不会留下一条被无端推进过的排程。计数器不倒退：`wrongCount` 是**历史事实**
 * （"这个词一共错过几次"），撤销判定不该篡改它 —— 而且倒退会让
 * `resolved_at >= last_wrong_at` 那条 CHECK 变得没有意义。
 *
 * ## 时间：日期按**家庭时区**算，不按服务器时区
 *
 * 服务器时区是 Asia/Tokyo、家庭时区是 Asia/Shanghai（见 `time.ts` 的文件头）。
 * "1 天后"如果按服务器时区算，晚上 23:30 批改就会排到"后天"。
 */

import {
  addDays,
  localDateTimeUtc,
  todayLocal,
} from '../time.js';
import { DEFAULT_FAMILY_TIMEZONE } from '../constants.js';

// ---------------------------------------------------------------- 批改取值

/**
 * 一道题的批改结果（`dictation_question.mark`）。
 *
 * `null` = **还没批改**（不是"对了"、也不是"错了"）。
 * 三者必须分得开：家长还没批改的会话不该被算成"全对"。
 */
export const DICTATION_MARKS = ['correct', 'wrong', 'missing'] as const;
export type DictationMark = (typeof DICTATION_MARKS)[number];

/**
 * `counted_mark` 的取值 = `DICTATION_MARKS` + `'none'`。
 *
 * 为什么需要它：批改是**可重复提交**的（家长点错了要能改），而"重复提交不能
 * 重复累加错词次数"是本轮的硬要求。做法是把"上一次对这条错词生效的判定"记在
 * 题目的 `counted_mark` 列上（**每个题一行，没有第二张流水表**）：
 *
 *   · 新判定 === `counted_mark` → 什么都没变，**跳过**（这就是幂等）；
 *   · 不同 → 先按旧的撤回、再按新的生效（这就是"可修改"）。
 *
 * `'none'` 与 `NULL` 是**两件事**：
 *   · `NULL` —— 这道题**从没被批改过**；
 *   · `'none'` —— 被批改过，结果是"对"或"回退"，**对错词计数没有贡献**。
 *
 * ⚠️ 用 `'none'`（一个带 CHECK 的取值）而不是"用一个时间戳判断有没有批改过"：
 *   时间戳那种写法在时钟回拨/手工改库时会静默失效，而**两个字符串比大小永远可靠**。
 */
export const COUNTED_MARKS = ['correct', 'wrong', 'missing', 'none'] as const;
export type CountedMark = (typeof COUNTED_MARKS)[number];

/** SQL 片段用的取值列表（数据库 CHECK 与这里逐字一致，只靠注释与用例钉住）。 */
export const DICTATION_MARK_VALUES = DICTATION_MARKS;
export const COUNTED_MARK_VALUES = COUNTED_MARKS;

/** 这个判定对**错词本**的贡献：`wrong`/`missing` 各 +1，`correct` 与"没批改"是 0。 */
export function mistakeCountsAsError(
  mark: CountedMark | null,
): mark is 'wrong' | 'missing' {
  return mark === 'wrong' || mark === 'missing';
}

/**
 * **批改改判定时的回退**：把上一次判定的影响撤掉，**连计数一起撤**。
 *
 * ## 与 `transitionMistake(state, null)` 的区别（2026-10-29 用户要求）
 *
 * 用户原话：
 *
 * > "第一次批改时，假设有错字一个，那么保存后，错字就是一个。然后第二次把那个错字
 * >  改成正确，再保存，还是显示错字 1 个，然后第三次又把那个字改成错字，保存，
 * >  这时就显示这个词错了两次了。正确的做法应该是更改数据，而不是叠加数据。"
 *
 * 也就是说：**一道题对错词本的贡献由它"最终判定"决定，改判要做"替换"而不是"再记一笔"**：
 *
 * | 序列 | 旧行为（只加不减） | 现在 |
 * | --- | --- | --- |
 * | 错 → 对 | 计数仍 1 | 计数 0（**这一行随之删掉**，见 grading service） |
 * | 错 → 对 → 错 | 2 次 | 1 次 |
 * | 错 → 缺失 | 2 次 | 1 次（`wrong` 撤掉、`missing` 记上） |
 *
 * `transitionMistake(state, null)` 保留原来"只回退档位与排程、不动计数"的语义 ——
 * 它仍然是**错词本手动操作**（标记已掌握 / 撤销）用的那条路。批改场景改用本函数。
 */
export function revertGradingImpact(
  state: MistakeScheduleState,
  previous: 'wrong' | 'missing',
  now: Date,
  options: { hour?: number; timeZone?: string } = {},
): MistakeScheduleState {
  /** 档位、排程、连对清零等仍走唯一的状态迁移（`mark = null` 那一支）。 */
  const base = transitionMistake(state, null, now, options);
  return {
    ...base,
    wrongCount: previous === 'wrong' ? Math.max(base.wrongCount - 1, 0) : base.wrongCount,
    missingCount:
      previous === 'missing' ? Math.max(base.missingCount - 1, 0) : base.missingCount,
  };
}

// ---------------------------------------------------------------- 间隔阶梯

/**
 * 间隔阶梯（**单位：天**）。下标 = `review_stage`。
 *
 * 0 是"还没错过（新排进来的）"，所以它不会被排程用到（没进阶梯就没有下次复习时间）。
 */
export const MISTAKE_REVIEW_INTERVAL_DAYS = [0, 1, 3, 7] as const;

/** 阶梯最大档（再错也停在这里，不会无限拉长）。`= 3`。 */
export const MISTAKE_MAX_REVIEW_STAGE = MISTAKE_REVIEW_INTERVAL_DAYS.length - 1;

/** 连续答对几次算掌握（消解）。 */
export const MISTAKE_MASTERY_STREAK = 2;

/**
 * 这一档的复习间隔（天）。
 *
 * 越界一律夹到 `[0, MISTAKE_MAX_REVIEW_STAGE]`：数据库那条
 * `review_stage BETWEEN 0 AND 3` 挡的是"有人绕过 API 写库"，这里是
 * "老行/手工改过的行读出来是 99 时不要算出 `undefined` 天"。
 */
export function reviewIntervalDays(stage: number): number {
  const index = Math.min(Math.max(Math.trunc(stage) || 0, 0), MISTAKE_MAX_REVIEW_STAGE);
  return MISTAKE_REVIEW_INTERVAL_DAYS[index] ?? 0;
}

/** 家长端的复习时段（家庭时区的整点）：下午 4 点，放学后、晚饭前。 */
export const MISTAKE_REVIEW_HOUR = 16;

/**
 * "从 `from` 这一天起 `days` 天后的复习时刻"。
 *
 * 刻意返回**具体时刻**（当天 16:00）而不是"日期"：
 *   · 排序、`<= now()` 判断、跨天比较都只需要一个时间戳，多存一个"日期"列
 *     就多一处可能与它不一致的值；
 *   · 界面直接 `formatDateTime` 就能印出"10月20日 16:00"，不需要再拼一次时间。
 */
export function nextReviewAt(
  from: Date,
  days: number,
  options: { hour?: number; timeZone?: string } = {},
): Date {
  const timeZone = options.timeZone ?? DEFAULT_FAMILY_TIMEZONE;
  const hour = options.hour ?? MISTAKE_REVIEW_HOUR;
  const localDate = addDays(todayLocal(from, timeZone), days);
  return localDateTimeUtc(localDate, `${String(hour).padStart(2, '0')}:00:00`, timeZone);
}

// ---------------------------------------------------------------- 状态与迁移

/**
 * 一条错词记录的**全部排程状态**（= `dictation_mistake` 那几列的纯数据形态）。
 *
 * 与数据库列一一对应，所以服务层可以整行读出来 → 交给 `transitionMistake`
 * → 整行写回去，中间不需要任何"翻译层"。
 */
export interface MistakeScheduleState {
  /** 累计写错次数（**历史事实，只增不减**）。 */
  wrongCount: number;
  /** 累计"没写出来"次数（同上）。 */
  missingCount: number;
  /** 最近一次出错的时间。从没错过就是 `null`。 */
  lastWrongAt: Date | null;
  /** 当前间隔档位（0~`MISTAKE_MAX_REVIEW_STAGE`）。 */
  reviewStage: number;
  /** 下次该复习的时间。`resolvedAt !== null` 时必须是 `null`。 */
  nextReviewAt: Date | null;
  /** 在复习里连续答对了几次。任何一次出错/回退都清零。 */
  streakCorrect: number;
  /** 从错词本里消解的时间（`null` = 还在错词本里）。 */
  resolvedAt: Date | null;
  /** 掌握的时间（家长手动消解时为 `null`）。 */
  masteredAt: Date | null;
  /** 消解方式：自动（连续答对）/ 家长手动。 */
  resolvedBy: 'auto' | 'manual' | null;
}

/** 一条新错词（第一次批改成 `wrong` / `missing` 时的初值）。 */
export function initialMistakeSchedule(): MistakeScheduleState {
  return {
    wrongCount: 0,
    missingCount: 0,
    lastWrongAt: null,
    reviewStage: 0,
    nextReviewAt: null,
    streakCorrect: 0,
    resolvedAt: null,
    masteredAt: null,
    resolvedBy: null,
  };
}

/** 家长手动"已掌握"时的状态（从错词本移出，但**计数与历史保留**）。 */
export function manualMastery(
  state: MistakeScheduleState,
  now: Date,
): MistakeScheduleState {
  return {
    ...state,
    nextReviewAt: null,
    streakCorrect: 0,
    resolvedAt: now,
    masteredAt: null,
    resolvedBy: 'manual',
  };
}

/** 家长撤销手动消解（"又发现不会写"）：重新排到 **1 天后**，计数不清零。 */
export function undoSchedule(state: MistakeScheduleState, now: Date): MistakeScheduleState {
  return {
    ...state,
    nextReviewAt: nextReviewAt(now, reviewIntervalDays(1)),
    streakCorrect: 0,
    resolvedAt: null,
    masteredAt: null,
    resolvedBy: null,
  };
}

/**
 * **唯一的状态迁移函数**：给定当前状态与"这次批改的判定"，返回新状态。
 *
 * @param state 当前状态（`dictation_mistake` 那一行；从没存在过就用
 *              `initialMistakeSchedule()`）
 * @param mark  这次的判定：`'wrong'` / `'missing'` / `'correct'`；
 *              **`null` = 回退**（把上一次判定的影响撤掉，家长点错了）
 * @param now   现在（批改时刻）
 *
 * 三个分支：
 *
 *   · **错（wrong / missing）**：计数 +1、`last_wrong_at = now`、档位 +1（到顶为止）、
 *     排到 `now + 阶梯[档位]` 天、连续答对清零。消解过的词**重新进错词本**
 *     （`resolved_at = NULL`）—— 既然又不会写了，就不该继续躺在"已掌握"里。
 *   · **对（correct）**：连续答对 +1；到 `MISTAKE_MASTERY_STREAK` 次 → 自动消解
 *     （`mastered_at = now`）；没到就**留在阶梯上**排下一天。
 *   · **回退（null）**：档位退一档（到 0 为止）、排回退档的间隔、连续答对清零，
 *     并恢复"未消解"。
 */
export function transitionMistake(
  state: MistakeScheduleState,
  mark: DictationMark | null,
  now: Date,
  options: { hour?: number; timeZone?: string } = {},
): MistakeScheduleState {
  const schedule = (days: number): Date => nextReviewAt(now, days, options);

  if (mark === 'wrong' || mark === 'missing') {
    /**
     * 档位推进：**先 +1 再取值**。从"还没错过"（0）第一次错 → 档位 1 → 1 天。
     * 到顶（`MISTAKE_MAX_REVIEW_STAGE`）为止，再错也还是 7 天。
     */
    const stage = Math.min(state.reviewStage + 1, MISTAKE_MAX_REVIEW_STAGE);
    return {
      ...state,
      wrongCount: state.wrongCount + (mark === 'wrong' ? 1 : 0),
      missingCount: state.missingCount + (mark === 'missing' ? 1 : 0),
      lastWrongAt: now,
      reviewStage: stage,
      nextReviewAt: schedule(reviewIntervalDays(stage)),
      streakCorrect: 0,
      /**
       * 又错了 → 从"已掌握"里出来。⚠️ `wrong_count` 之类**不清零**：
       * 那是历史事实，而且"这个词错过 4 次"正是家长判断要不要重点辅导的依据。
       */
      resolvedAt: null,
      masteredAt: null,
      resolvedBy: null,
    };
  }

  if (mark === 'correct') {
    const streak = state.streakCorrect + 1;
    if (streak >= MISTAKE_MASTERY_STREAK) {
      return {
        ...state,
        streakCorrect: streak,
        nextReviewAt: null,
        resolvedAt: now,
        masteredAt: now,
        resolvedBy: 'auto',
      };
    }
    /**
     * 还没到"连续答对 2 次"，**留在阶梯上**：按当前档位的间隔再排一天。
     *
     * ⚠️ 这一步**不降档**（不回退到更短的间隔）：档位是"这个词有多难"的记录，
     *   答对一次就把难度降回去，会让一个反复错的词一直被排在最前面。
     */
    return {
      ...state,
      streakCorrect: streak,
      nextReviewAt: schedule(reviewIntervalDays(state.reviewStage)),
      resolvedAt: null,
      masteredAt: null,
      resolvedBy: null,
    };
  }

  // ---- 回退（mark === null）：把上一次批改的影响撤掉 ----
  /**
   * ⚠️ 这里对 `streakCorrect` **不做递减、直接清零**，并把档位夹在 `[0, 上限]` 内。
   *
   * 两个都不是"顺手为之"，而是**数据库约束的硬要求**：
   *   · `ck_dictation_mistake_streak` 要求 `streak_correct <= 2`。如果回退时
   *     `-1`，那么"连对 2 次（=2）→ 回退（=1）"没问题，但"连对 2 次 → 又错（=0）
   *     → 再连对 2 次（=2）→ 回退"这类序列会算出 3，写库当场报错 23514。
   *     而"回退一次"在语义上本来就该把连对记录清掉（那次判定当它没发生），
   *     清零既安全又更符合直觉。
   *   · `ck_dictation_mistake_stage` 要求 `review_stage <= 3`，同一个道理。
   */
  const stage = Math.min(Math.max(state.reviewStage - 1, 0), MISTAKE_MAX_REVIEW_STAGE);
  return {
    ...state,
    reviewStage: stage,
    /**
     * 回退到第 0 档（还是"新错词"）时用 1 天 —— 不能用 `reviewIntervalDays(0)`
     * （那是 0 天 = 今天就到期，而"今天就再来一遍"不是家长点错一次该有的结果）。
     */
    nextReviewAt: schedule(reviewIntervalDays(Math.max(stage, 1))),
    streakCorrect: 0,
    resolvedAt: null,
    masteredAt: null,
    resolvedBy: null,
  };
}

// ---------------------------------------------------------------- 人话

/**
 * 阶梯的**人话说明**（界面上必须原样出现一句）。
 *
 * 单独一个函数而不是把这句话写在两个页面里：家长在批改页与错词本上看到的
 * 规则必须**一模一样**，否则"系统是不是在偷偷乱排"这个问题会立刻冒出来。
 */
export function describeReviewLadder(): string {
  const ladder = MISTAKE_REVIEW_INTERVAL_DAYS.slice(1).join(' 天 → ');
  return `错的词会自动排进下一次听写：第 1 次错 → ${ladder} 天后再听，到顶就一直是 ${MISTAKE_REVIEW_INTERVAL_DAYS[MISTAKE_MAX_REVIEW_STAGE]} 天；连续答对 ${MISTAKE_MASTERY_STREAK} 次就算掌握，不再自动排进来。`;
}

/** 一条错词的排程 → 人话（错词本每一行都显示它）。 */
export function describeMistakeSchedule(state: {
  resolvedAt: Date | string | null;
  resolvedBy: 'auto' | 'manual' | null;
  nextReviewAt: Date | string | null;
  reviewStage: number;
}): string {
  if (state.resolvedAt !== null) {
    return state.resolvedBy === 'manual' ? '已掌握（手动标记）' : '已掌握（连续答对）';
  }
  if (state.nextReviewAt === null) return '没有排下次复习 —— 这是数据异常，请刷新';
  const days = reviewIntervalDays(state.reviewStage);
  return `下次复习排在第 ${days} 天档`;
}
